/**
 * List Node Item Component
 *
 * Renders a single node in the list view with expand/collapse, selection,
 * and edit/delete/add-child/add-parent/transform/ingredient/elaboration
 * buttons. Editing opens the shared NodeEditPopup (same effect as the
 * EditToolbar's global edit button) — only on narrow screens, where the
 * popup is the only way to edit; on wide screens the side panel already
 * shows the same editable tabs inline, so the button is hidden there too
 * (mirrors the EditToolbar's own edit button). Deleting follows Graph
 * View's two-click arm/confirm pattern.
 *
 * [↧]/[↥]/[↟] (add child/parent, transform) and [⚘]/[⚗]/[⚗ׯ] (go to
 * ingredient, add/modify/remove elaboration) used to live in the shared
 * EditToolbar (visible regardless of active tab) — moved here since they
 * apply per node, same as edit/delete, and this row already has
 * everything needed to show them right where the node itself is. [⚗]/
 * [⚗ׯ] stay independent of edit mode: viewing/adjusting an elaboration
 * isn't itself an edit-tracked change (same reasoning EditToolbar's own
 * version used). [⚘] (go to ingredient) is edit-mode-gated the other way
 * instead — hidden while editing, since it navigates away from the node
 * you're mid-edit on; only shown while just browsing.
 */

import { ListNode, ListViewConfig } from './types.js';
import { resolveNodeTitle } from '../recipe_renderer/DisplayGraph.js';
import { getNodeImageUrl as resolveNodeImageUrl } from '../recipe_renderer/imageUtils.js';
import { buildNodeDetailCard } from '../recipe_renderer/components/NodeDetailCard.js';
import type { RecipeStore } from '../store/types.js';

export class ListNodeItem {
  private node: ListNode;
  private config: ListViewConfig;
  private editMode: boolean;
  private deleteArmed: boolean;
  private showEditButton: boolean;
  private multiSelectMode: boolean;
  private store: RecipeStore;
  private callbacks: {
    onToggleExpand: () => void;
    onToggleSelect: () => void;
    onEditClick: () => void;
    onDeleteClick: () => void;
    onGoToIngredient: () => void;
    onElaborationClick: () => void;
    onRemoveElaboration: () => void;
    onAddChild: () => void;
    onAddParent: () => void;
    onTransform: () => void;
  };

  constructor(
    node: ListNode,
    config: ListViewConfig,
    editMode: boolean,
    deleteArmed: boolean,
    showEditButton: boolean,
    multiSelectMode: boolean,
    store: RecipeStore,
    callbacks: {
      onToggleExpand: () => void;
      onToggleSelect: () => void;
      onEditClick: () => void;
      onDeleteClick: () => void;
      onGoToIngredient: () => void;
      onElaborationClick: () => void;
      onRemoveElaboration: () => void;
      onAddChild: () => void;
      onAddParent: () => void;
      onTransform: () => void;
    }
  ) {
    this.node = node;
    this.config = config;
    this.editMode = editMode;
    this.deleteArmed = deleteArmed;
    this.showEditButton = showEditButton;
    this.multiSelectMode = multiSelectMode;
    this.store = store;
    this.callbacks = callbacks;
  }

  /**
   * Render the list node item
   */
  render(): HTMLElement {
    // "Expanding" (full title, image, other details replacing the row's
    // own truncated name) is a single-select affordance — a node that's
    // merely part of a multi-select doesn't get it, same reasoning
    // showEditButton already applies to the edit/delete buttons.
    const expanded = this.node.isSelected && !this.multiSelectMode;

    const item = document.createElement('div');
    item.className = 'list-node-item';
    // No background/padding here — the row itself spans the full
    // available width; the header/detail split below carries its own
    // background (matching the explorer's own summary-row/expanded-
    // content split), and the padding that insets the arrow/image/text/
    // buttons lives on `inner` instead.
    item.style.cssText = `
      border-left: 2px solid ${expanded ? 'var(--accent)' : 'transparent'};
      border-bottom: 1px solid var(--accent-alpha-13);
      transition: background 0.2s;
      cursor: pointer;
    `;

    // Click anywhere on the item to select it (in and out of edit mode) —
    // mirrors clicking a node in Graph View, which always selects regardless
    // of mode.
    item.addEventListener('click', (e) => {
      // Don't trigger if clicking on buttons or arrow
      if ((e.target as HTMLElement).closest('button, .list-arrow')) {
        return;
      }
      this.callbacks.onToggleSelect();
    });

    // Indentation lives here, as extra left padding scaled by nesting
    // depth — not as margin on some ancestor wrapper (see ListView.ts's
    // renderListNode() for why: that shrinks every row's own box instead
    // of just shifting it, so it could never span the list's true width).
    // Expanded rows are the one exception: the caret sits at the very
    // left regardless of depth, since there's no sibling content left to
    // indent alongside it anyway (see below) — the real detail is in the
    // block underneath, which has its own fixed padding either way.
    const indentLeft = 12 + (expanded ? 0 : this.node.level * this.config.indentPerLevel);
    const inner = document.createElement('div');
    // The header line — same highlighted background a selected/multi-
    // selected row always had, just now scoped to this line instead of
    // the whole row (see the detail block below for its own, darker one).
    inner.style.cssText = `
      display: flex;
      align-items: center;
      padding: 8px 12px 8px ${indentLeft}px;
      background: ${(this.node.isSelected || expanded) ? 'var(--accent-alpha-13)' : 'transparent'};
    `;
    item.appendChild(inner);

    // Expand/collapse arrow — every node with children except a recipe's
    // own main root, which gets no caret at all: folding/unfolding it
    // also focuses/unfocuses the recipe, and that's exclusively the
    // focus button's (⌖) job now (renderButtons(), right-aligned, shown
    // once the row is expanded) rather than something a plain chevron in
    // this slot would do too. Main roots skip the spacer too, below —
    // every main root lacks a caret alike (they're only ever siblings of
    // other main roots), so there's nothing to line up with. A leaf
    // *nested* under some parent is different: its siblings there can
    // easily have children of their own (and thus a caret), so it still
    // needs the same reserved width to line up with them.
    if (!this.node.displayNode.isMainRoot) {
      if (this.node.displayNode.data.children.length > 0) {
        inner.appendChild(this.renderArrow());
      } else {
        const spacer = document.createElement('div');
        spacer.style.cssText = 'width: 20px; flex-shrink: 0; margin-right: 4px;';
        inner.appendChild(spacer);
      }
    }

    // Node content (image + name) — omitted once expanded, same "title
    // moves into the expanded detail below, not truncated there" swap
    // the ingredients/transformations/tags explorers already do for their
    // own expanded row (see ExplorerPage.ts/TagsExplorerPage.ts). A
    // spacer keeps the arrow/buttons aligned exactly like the image+name
    // otherwise would.
    if (expanded) {
      const spacer = document.createElement('div');
      spacer.style.cssText = 'flex: 1; min-width: 0;';
      inner.appendChild(spacer);
    } else {
      const content = this.renderContent();
      inner.appendChild(content);
    }

    // Ingredient/elaboration/edit/delete buttons — only on the one
    // expanded row (not during multi-select, same as the detail block
    // below — a single-target action row wouldn't mean much there).
    if (expanded) {
      const buttons = this.renderButtons();
      if (buttons.childElementCount > 0) inner.appendChild(buttons);
    }

    // Selecting a node "expands" it — the same effect selecting a row in
    // the ingredients/transformations explorer has — showing its full
    // (never truncated) title, image, and the other information the node
    // visualizer currently shows (quantity, tags, notes). A step toward
    // eventually retiring the node visualizer altogether. Not while
    // multi-selecting — see `expanded`'s own comment above.
    if (expanded) {
      item.appendChild(this.renderSelectedDetail());
    }

    return item;
  }



  /**
   * Render the main root's focus button — shown only in the expanded
   * row's own button group (renderButtons()), right-aligned like every
   * other button there, rather than in the row's arrow slot (which still
   * shows the plain chevron for a main root, same as any other node with
   * children — see render()). Filled while this recipe is the focused
   * one (isExpanded doubles as "is focused" for a main root — see
   * RecipeStore.expandNode()/collapseNode()), exactly like the explorer's
   * own focus button. Clicking it fires the same onToggleExpand callback
   * the plain arrow uses; ListView routes a main root's own callback to
   * focus/unfocus rather than a plain fold (see handleToggleExpand()'s
   * own doc comment).
   */
  private renderFocusButton(): HTMLElement {
    const isFocused = this.node.isExpanded;
    return this.buildRoundButton(
      '⌖',
      isFocused ? 'Torna a la llista' : 'Centra',
      () => this.callbacks.onToggleExpand(),
      isFocused,
    );
  }

  /**
   * Render expand/collapse arrow
   */
  private renderArrow(): HTMLElement {
    const arrow = document.createElement('div');
    arrow.className = 'list-arrow';
    arrow.style.cssText = `
      width: 20px;
      height: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      color: var(--accent);
      font-size: 12px;
      flex-shrink: 0;
      margin-right: 4px;
      user-select: none;
    `;
    arrow.textContent = this.node.isExpanded ? '▼' : '▶';
    arrow.addEventListener('click', (e) => {
      e.stopPropagation();
      this.callbacks.onToggleExpand();
    });
    return arrow;
  }

  /**
   * Render node content (image + name) — no description, matching the
   * explorer's own collapsed rows (which don't show one either).
   */
  private renderContent(): HTMLElement {
    const content = document.createElement('div');
    content.className = 'list-node-content';
    content.style.cssText = `
      flex: 1;
      min-width: 0;
      display: flex;
      align-items: center;
      gap: 8px;
    `;

    // Image thumbnail (circular) — a node with no image of its own gets
    // the app's own logo icon on a black circle instead, rather than
    // nothing at all.
    const imageUrl = this.getNodeImageUrl();
    if (imageUrl) {
      const img = document.createElement('img');
      img.src = imageUrl;
      img.alt = '';
      img.style.cssText = `
        width: 28px;
        height: 28px;
        border-radius: 50%;
        object-fit: cover;
        flex-shrink: 0;
      `;
      content.appendChild(img);
    } else {
      const placeholder = document.createElement('div');
      placeholder.style.cssText = `
        width: 28px;
        height: 28px;
        border-radius: 50%;
        background: #1a1a2e;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
      `;
      const icon = document.createElement('img');
      icon.src = '/static/img/coberts.png';
      icon.alt = '';
      icon.style.cssText = 'width: 16px; height: 16px; object-fit: contain;';
      placeholder.appendChild(icon);
      content.appendChild(placeholder);
    }

    // Text content
    const textContent = document.createElement('div');
    textContent.style.cssText = `
      flex: 1;
      min-width: 0;
    `;

    // Node name.
    const name = document.createElement('div');
    name.style.cssText = `
      display: flex;
      align-items: center;
      gap: 4px;
      color: #e0e0e0;
      font-size: 14px;
      min-width: 0;
    `;

    const nameText = document.createElement('span');
    nameText.style.cssText = 'white-space: nowrap; overflow: hidden; text-overflow: ellipsis; min-width: 0;';
    nameText.textContent = resolveNodeTitle(this.node.displayNode.data);
    name.appendChild(nameText);

    textContent.appendChild(name);

    content.appendChild(textContent);

    return content;
  }

  /**
   * The expanded detail shown below a selected row:
   *   (image), full title, node description, ingredient description,
   *   quantity
   * — and, only once this node's own children are folded open (isExpanded
   * — more of the recipe under it is actually visible), also:
   *   preparation text, transformation description
   * A step toward eventually retiring the node visualizer altogether.
   */
  private renderSelectedDetail(): HTMLElement {
    return buildNodeDetailCard(this.node.displayNode, this.store, {
      showPreparationText: this.node.isExpanded,
    });
  }

  /**
   * Render this row's action buttons — edit/delete (edit mode only, same
   * as before), plus go-to-ingredient/elaboration (independent of edit
   * mode — see this file's own doc comment for why). Edit opens the
   * shared NodeEditPopup (same effect as the EditToolbar's global edit
   * button). Delete matches Graph View's two-click arm/confirm pattern:
   * first click arms it (highlighted), a second click on the same button
   * confirms the deletion. Clicking elsewhere disarms it (handled by
   * ListView, which owns the armed state).
   */
  private renderButtons(): HTMLElement {
    const buttons = document.createElement('div');
    buttons.style.cssText = `
      display: flex;
      gap: 4px;
      flex-shrink: 0;
    `;

    // Focus toggle — a recipe's main root only, first in the group (same
    // position the explorer's own focus button takes), right-aligned
    // with everything else here rather than in the row's own arrow slot.
    if (this.node.displayNode.isMainRoot) {
      buttons.appendChild(this.renderFocusButton());
    }

    if (this.editMode) {
      buttons.appendChild(this.buildRoundButton('↧', 'Afegeix un node fill', () => this.callbacks.onAddChild()));
      buttons.appendChild(this.buildRoundButton('↥', 'Afegeix un node pare', () => this.callbacks.onAddParent()));
      buttons.appendChild(this.buildRoundButton('↟', 'Transforma el node', () => this.callbacks.onTransform()));
    }

    if (this.editMode && this.showEditButton) {
      buttons.appendChild(this.buildRoundButton('✎', 'Edita', () => this.callbacks.onEditClick()));
    }

    if (!this.editMode && this.hasIngredient()) {
      buttons.appendChild(this.buildRoundButton('⚘', 'Ves a l\'ingredient', () => this.callbacks.onGoToIngredient()));
    }

    if (this.canElaborate()) {
      const isLeaf = this.isLeaf();
      if (isLeaf) {
        buttons.appendChild(this.buildRoundButton(
          '⚗', 'Afegeix elaboració', () => this.callbacks.onElaborationClick(),
        ));
      } else {
        buttons.appendChild(this.buildCompositeRoundButton(
          '⚗', '×', 'Esborra elaboració', () => this.callbacks.onRemoveElaboration(),
        ));
      }
    }

    if (this.editMode) {
      const deleteBtn = document.createElement('button');
      deleteBtn.title = this.deleteArmed ? 'Confirma l\'esborrat' : 'Esborra';
      deleteBtn.style.cssText = `
        width: 30px;
        height: 30px;
        border: 1px solid ${this.deleteArmed ? 'var(--accent)' : 'var(--accent-alpha-40)'};
        background: ${this.deleteArmed ? 'var(--accent)' : 'transparent'};
        color: ${this.deleteArmed ? '#0d1117' : 'var(--accent)'};
        border-radius: 50%;
        cursor: pointer;
        font-size: 18px;
        font-weight: bold;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.15s ease;
        flex-shrink: 0;
      `;
      // The × glyph sits visually high in its line box — nudge it down so it
      // looks centered in the round button (same correction Graph View's SVG
      // buttons apply via a baseline dy offset).
      const glyph = document.createElement('span');
      glyph.textContent = '×';
      glyph.style.cssText = 'display: inline-block; transform: translate(0px, 2px);';
      deleteBtn.appendChild(glyph);
      deleteBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.callbacks.onDeleteClick();
      });
      buttons.appendChild(deleteBtn);
    }

    return buttons;
  }

  /**
   * Plain round icon button — same look as every other one in this row.
   * `active` fills it solid (matching the explorer's own focus button —
   * ExplorerPage.ts's buildFocusIconButton()/buildIconButton()) instead of
   * the usual outlined look, for a toggled-on state (e.g. focused).
   */
  private buildRoundButton(icon: string, title: string, onClick: () => void, active: boolean = false): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.title = title;
    btn.textContent = icon;
    btn.style.cssText = `
      width: 30px;
      height: 30px;
      border: 1px solid ${active ? 'var(--accent)' : 'var(--accent-alpha-40)'};
      background: ${active ? 'var(--accent)' : 'transparent'};
      color: ${active ? '#0d1117' : 'var(--accent)'};
      border-radius: 50%;
      cursor: pointer;
      font-size: 15px;
      font-weight: bold;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.15s ease;
      flex-shrink: 0;
    `;
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      onClick();
    });
    return btn;
  }

  /**
   * Same round button as buildRoundButton(), but with a small badge glyph
   * overlaid at the bottom-right corner of the main icon (e.g. the
   * alembic with a × badge for "remove elaboration") — mirrors the
   * EditToolbar's own makeCompositeIconButton(), which this replaces.
   */
  private buildCompositeRoundButton(baseIcon: string, badgeIcon: string, title: string, onClick: () => void): HTMLButtonElement {
    const btn = this.buildRoundButton(baseIcon, title, onClick);
    btn.textContent = '';
    btn.style.position = 'relative';

    const base = document.createElement('span');
    base.textContent = baseIcon;
    base.style.pointerEvents = 'none';
    btn.appendChild(base);

    const badge = document.createElement('span');
    badge.textContent = badgeIcon;
    badge.style.cssText = `
      position: absolute;
      bottom: -3px;
      right: -3px;
      width: 13px;
      height: 13px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 10px;
      font-weight: bold;
      line-height: 1;
      color: #ef4444;
      background: #0d1117;
      border: 1px solid #ef4444;
      border-radius: 50%;
      pointer-events: none;
    `;
    btn.appendChild(badge);

    return btn;
  }

  /** Whether this node has an ingredient — drives [⚘]'s visibility. */
  private hasIngredient(): boolean {
    return !!this.node.displayNode.data.ingredientInfo?.ingredient;
  }

  /**
   * Whether this node can be "elaborated" at all — it must have an
   * ingredient and not be a root node. Independent of edit mode: viewing/
   * adjusting a node's elaboration isn't itself an edit-tracked change.
   * Mirrors GraphVisualizer's own (now removed) canElaborateSelectedNode().
   */
  private canElaborate(): boolean {
    if (!this.hasIngredient()) return false;
    return this.store.getParentIds(this.node.displayNode.id).length > 0;
  }

  /** Whether this node is a leaf (no children) — only meaningful when canElaborate() is true. */
  private isLeaf(): boolean {
    const children = this.node.displayNode.data.children;
    return !children || children.length === 0;
  }

  /**
   * Get node image URL — a node's own explicit imageUrl, no more derivation.
   */
  private getNodeImageUrl(): string | null {
    return resolveNodeImageUrl(this.node.displayNode.data) ?? null;
  }
}
